import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { Plus, Search, Filter, Edit, Eye, TrendingDown, Trash2, X } from 'lucide-react';
import { toast } from 'react-toastify';
import Button from '../../components/UI/Button';
import Input from '../../components/UI/Input';
import { TableRoot, Table, THead, TR, TH, TBody, TD } from '../../components/UI/Table';
import LoadingSpinner from '../../components/UI/LoadingSpinner';
import { Expense } from '../../lib/sdk/expenses';
import { useAuthStore } from '../../stores/authStore';
import { useExpenses, useExpenseTypes, useAccounts, useUpdateExpense, useDeleteExpense, useExpenseStats } from '../../hooks/useQueries';
import { getErrorMessage } from '../../lib/utils/errorMessage';

// Custom debounce hook
const useDebounce = (value: string, delay: number) => {
  const [debouncedValue, setDebouncedValue] = useState(value);

  useEffect(() => {
    const handler = setTimeout(() => {
      setDebouncedValue(value);
    }, delay);

    return () => {
      clearTimeout(handler);
    };
  }, [value, delay]);

  return debouncedValue;
};

const ManageExpenses: React.FC = () => {
  const navigate = useNavigate();
  const { user } = useAuthStore();
  
  // Filters
  const [searchTerm, setSearchTerm] = useState('');
  const [expenseTypeFilter, setExpenseTypeFilter] = useState('');
  const [startDate, setStartDate] = useState('');
  const [endDate, setEndDate] = useState('');
  
  // Pagination
  const [currentPage, setCurrentPage] = useState(1);
  
  // Search params for React Query
  const [searchParams, setSearchParams] = useState({
    page: 1,
    limit: 50,
    search: '',
    expenseTypeId: '',
    startDate: '',
    endDate: ''
  });
  
  // Use React Query hooks
  const { data: expensesResponse, isLoading: expensesLoading, refetch: refetchExpenses } = useExpenses(searchParams);
  const { data: expenseTypesData = [] } = useExpenseTypes();
  const { data: accountsResponse } = useAccounts({ limit: 50 });
  const { data: statsData } = useExpenseStats({ startDate, endDate });
  
  // Extract data from responses
  const expenses = expensesResponse?.data || (Array.isArray(expensesResponse) ? expensesResponse : []);
  const expenseTypes = Array.isArray(expenseTypesData) ? expenseTypesData.filter((et: any) => et.isActive) : [];
  const accounts = accountsResponse?.data || (Array.isArray(accountsResponse) ? accountsResponse : []);
  const stats = statsData?.data || statsData || { totalExpenses: 0, totalAmount: 0, averageAmount: 0 };
  const totalPages = expensesResponse?.pagination?.totalPages || 1;
  const totalExpenses = expensesResponse?.pagination?.total || expenses.length;
  
  const tableLoading = expensesLoading;
  
  // Modal states
  const [showViewModal, setShowViewModal] = useState(false);
  const [showEditModal, setShowEditModal] = useState(false);
  const [selectedExpense, setSelectedExpense] = useState<Expense | null>(null);
  const [editFormData, setEditFormData] = useState({
    name: '',
    amount: '',
    expenseTypeId: '',
    accountId: ''
  });
  
  const debouncedSearchTerm = useDebounce(searchTerm, 500);

  // Update search params when filters change
  useEffect(() => {
    setSearchParams({
      page: currentPage,
      limit: 50,
      search: debouncedSearchTerm,
      expenseTypeId: expenseTypeFilter,
      startDate,
      endDate
    });
  }, [currentPage, debouncedSearchTerm, expenseTypeFilter, startDate, endDate]);

  // Mutations
  const updateExpenseMutation = useUpdateExpense({
    onSuccess: () => {
      toast.success('Expense updated successfully');
      closeModal();
      refetchExpenses();
    },
    onError: (error: any) => {
      console.error('Error updating expense:', error);
      toast.error(getErrorMessage(error, 'Failed to update expense'));
    }
  });

  const deleteExpenseMutation = useDeleteExpense({
    onSuccess: () => {
      toast.success('Expense deleted successfully');
      closeModal();
      refetchExpenses();
    },
    onError: (error: any) => {
      console.error('Error deleting expense:', error);
      toast.error(getErrorMessage(error, 'Failed to delete expense'));
    }
  });

  // Handle search
  const handleSearch = (e: React.FormEvent) => {
    e.preventDefault();
    setCurrentPage(1); // Reset to first page on search
  };

  // Clear filters
  const clearFilters = () => {
    setSearchTerm('');
    setExpenseTypeFilter('');
    setStartDate('');
    setEndDate('');
    setCurrentPage(1); // Reset to first page on clear
  };

  // Handle page change
  const handlePageChange = (newPage: number) => {
    setCurrentPage(newPage);
  };

  // Check if expense can be edited (within 3 days or user is SuperAdmin)
  const canEditExpense = (expense: Expense) => {
    const threeDaysAgo = new Date();
    threeDaysAgo.setDate(threeDaysAgo.getDate() - 3);
    const expenseDate = new Date(expense.createdAt);
    
    // Check if user is super admin
    const isSuperAdmin = user?.role === 'SuperAdmin';
    
    return expenseDate > threeDaysAgo || isSuperAdmin;
  };

  // Modal handlers
  const openViewModal = (expense: Expense) => {
    setSelectedExpense(expense);
    setShowViewModal(true);
  };

  const openEditModal = (expense: Expense) => {
    if (!canEditExpense(expense)) {
      toast.error('Cannot edit expenses older than 3 days. Only SuperAdmin can modify old records.');
      return;
    }
    
    setSelectedExpense(expense);
    setEditFormData({
      name: expense.name,
      amount: expense.amount.toString(),
      expenseTypeId: expense.expenseTypeId._id,
      accountId: typeof expense.accountId === 'string' ? expense.accountId : expense.accountId?._id || ''
    });
    setShowEditModal(true);
  };

  const closeModal = () => {
    setShowViewModal(false);
    setShowEditModal(false);
    setSelectedExpense(null);
    setEditFormData({ name: '', amount: '', expenseTypeId: '', accountId: '' });
  };

  // Handle edit form submission
  const handleEditSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!selectedExpense) return;

    updateExpenseMutation.mutate({
      id: selectedExpense._id,
      data: {
        name: editFormData.name.trim(),
        amount: parseInt(editFormData.amount),
        expenseTypeId: editFormData.expenseTypeId,
        accountId: editFormData.accountId
      }
    });
  };

  // Handle delete
  const handleDelete = () => {
    if (!selectedExpense) return;
    deleteExpenseMutation.mutate(selectedExpense._id);
  };

  if (expensesLoading) {
    return <LoadingSpinner />;
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div className="flex items-center space-x-3">
          <div className="p-2 bg-primary-100 dark:bg-primary-900 rounded-lg">
            <TrendingDown className="h-8 w-8 text-primary-600 dark:text-primary-400" />
          </div>
          <div>
            <h1 className="text-2xl font-bold text-gray-900 dark:text-white">Manage Expenses</h1>
            <p className="text-gray-600 dark:text-gray-400">View and manage organizational expenses for Central Office</p>
          </div>
        </div>
        <Button 
          variant="primary"
          leftIcon={<Plus className="h-4 w-4" />}
          onClick={() => navigate('/add-expense')}
        >
          Add Expense
        </Button>
      </div>

      {/* Statistics Cards */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
        <div className="card">
          <div className="flex items-center">
            <div className="p-2 bg-blue-100 dark:bg-blue-900 rounded-lg">
              <TrendingDown className="h-6 w-6 text-blue-600 dark:text-blue-400" />
            </div>
            <div className="ml-4">
              <p className="text-sm font-medium text-gray-600 dark:text-gray-400">Total Expenses</p>
              <p className="text-2xl font-bold text-gray-900 dark:text-white">{stats?.totalExpenses || 0}</p>
            </div>
          </div>
        </div>
        
        <div className="card">
          <div className="flex items-center">
            <div className="p-2 bg-green-100 dark:bg-green-900 rounded-lg">
              <TrendingDown className="h-6 w-6 text-green-600 dark:text-green-400" />
            </div>
            <div className="ml-4">
              <p className="text-sm font-medium text-gray-600 dark:text-gray-400">Total Amount</p>
              <p className="text-2xl font-bold text-gray-900 dark:text-white">
                Rs {(stats?.totalAmount || 0).toLocaleString()}
              </p>
            </div>
          </div>
        </div>
        
        <div className="card">
          <div className="flex items-center">
            <div className="p-2 bg-purple-100 dark:bg-purple-900 rounded-lg">
              <TrendingDown className="h-6 w-6 text-purple-600 dark:text-purple-400" />
            </div>
            <div className="ml-4">
              <p className="text-sm font-medium text-gray-600 dark:text-gray-400">Average Amount</p>
              <p className="text-2xl font-bold text-gray-900 dark:text-white">
                Rs {(stats?.averageAmount || 0).toLocaleString()}
              </p>
            </div>
          </div>
        </div>
      </div>

      {/* Search and Filters */}
      <div className="card">
        <form onSubmit={handleSearch} className="space-y-4">
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
            <div>
              <label className="form-label">Search</label>
              <div className="relative">
                <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 h-4 w-4" />
                <Input
                  type="text"
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  placeholder="Search by name..."
                  className="pl-10"
                />
              </div>
            </div>
            
            <div>
              <label className="form-label">Expense Type</label>
              <select
                value={expenseTypeFilter}
                onChange={(e) => setExpenseTypeFilter(e.target.value)}
                className="form-select"
              >
                <option value="">All Types</option>
                {expenseTypes && expenseTypes.length > 0 ? expenseTypes.map((type) => (
                  <option key={type._id} value={type._id}>
                    {type.name}
                  </option>
                )) : (
                  <option value="" disabled>Loading expense types...</option>
                )}
              </select>
            </div>
            
            <div>
              <label className="form-label">Start Date</label>
              <Input
                type="date"
                value={startDate}
                onChange={(e) => setStartDate(e.target.value)}
                placeholder="Start Date"
              />
            </div>

            <div>
              <label className="form-label">End Date</label>
              <Input
                type="date"
                value={endDate}
                onChange={(e) => setEndDate(e.target.value)}
                placeholder="End Date"
              />
            </div>
          </div>
          
          <div className="flex space-x-2">
            <Button type="submit" variant="primary" leftIcon={<Filter className="h-4 w-4" />}>
              Apply Filters
            </Button>
            <Button type="button" variant="secondary" onClick={clearFilters}>
              Clear Filters
            </Button>
          </div>
        </form>
      </div>

      {/* Expenses Table */}
      <div className="card">
        {tableLoading ? (
          <div className="text-center py-12">
            <LoadingSpinner />
            <p className="mt-2 text-sm text-gray-500 dark:text-gray-400">Searching expenses...</p>
          </div>
        ) : expenses.length === 0 ? (
          <div className="text-center py-12">
            <TrendingDown className="h-12 w-12 text-gray-400 mx-auto mb-4" />
            <h3 className="text-lg font-medium text-gray-900 dark:text-white mb-2">No expenses found</h3>
            <p className="text-gray-500 dark:text-gray-400">
              {searchTerm || expenseTypeFilter || startDate || endDate
                ? 'Try adjusting your search criteria.'
                : 'Get started by adding your first expense.'}
            </p>
          </div>
        ) : (
          <>
            <TableRoot>
              <Table>
                <THead>
                  <TR>
                    <TH>ID</TH>
                    <TH>Name</TH>
                    <TH>Amount</TH>
                    <TH>Type</TH>
                    <TH>Date</TH>
                    <TH>Actions</TH>
                  </TR>
                </THead>
                <TBody>
                  {expenses.map((expense) => (
                    <TR key={expense._id}>
                      <TD className="font-mono text-sm">#{expense.expenseId}</TD>
                      <TD>
                        <div>
                          <div className="font-medium text-gray-900 dark:text-white">{expense.name}</div>
                        </div>
                      </TD>
                      <TD className="font-mono font-medium text-green-600 dark:text-green-400">
                        Rs {expense.amount.toLocaleString()}
                      </TD>
                      <TD>
                        <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-blue-100 text-blue-800 dark:bg-blue-900 dark:text-blue-200">
                          {expense.expenseTypeName}
                        </span>
                      </TD>
                      <TD className="text-sm text-gray-500 dark:text-gray-400">
                        {new Date(expense.createdAt).toLocaleDateString()}
                      </TD>
                      <TD>
                        <div className="flex space-x-2">
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={() => openViewModal(expense)}
                            leftIcon={<Eye className="h-4 w-4" />}
                          >
                            View
                          </Button>
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={() => openEditModal(expense)}
                            disabled={!canEditExpense(expense)}
                            leftIcon={<Edit className="h-4 w-4" />}
                            title={canEditExpense(expense) ? 'Edit expense' : 'Cannot edit expenses older than 3 days. Only SuperAdmin can modify old records.'}
                          >
                            Edit
                          </Button>
                        </div>
                      </TD>
                    </TR>
                  ))}
                </TBody>
              </Table>
            </TableRoot>
            
            {/* Pagination */}
            {totalPages > 1 && (
              <div className="px-6 py-4 bg-gray-50 dark:bg-gray-700 border-t border-gray-200 dark:border-gray-600">
                <div className="flex items-center justify-between">
                  <div className="text-sm text-gray-700 dark:text-gray-300">
                    Showing {((currentPage - 1) * 10) + 1} to{' '}
                    {Math.min(currentPage * 10, totalExpenses)} of{' '}
                    {totalExpenses} results
                  </div>
                  <div className="flex space-x-2">
                    <Button
                      variant="secondary"
                      size="sm"
                      onClick={() => handlePageChange(currentPage - 1)}
                      disabled={currentPage === 1}
                    >
                      Previous
                    </Button>
                    <span className="px-3 py-2 text-sm text-gray-700 dark:text-gray-300">
                      Page {currentPage} of {totalPages}
                    </span>
                    <Button
                      variant="secondary"
                      size="sm"
                      onClick={() => handlePageChange(currentPage + 1)}
                      disabled={currentPage === totalPages}
                    >
                      Next
                    </Button>
                  </div>
                </div>
              </div>
            )}
          </>
        )}
      </div>

      {/* View Modal */}
      {showViewModal && selectedExpense && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white dark:bg-gray-800 rounded-lg shadow-xl max-w-2xl w-full mx-4 max-h-[90vh] overflow-y-auto">
            <div className="flex items-center justify-between p-6 border-b border-gray-200 dark:border-gray-700">
              <h3 className="text-lg font-medium text-gray-900 dark:text-white">Expense Details</h3>
              <Button
                variant="ghost"
                size="sm"
                onClick={closeModal}
                leftIcon={<X className="h-4 w-4" />}
              >
                Close
              </Button>
            </div>
            
            <div className="p-6 space-y-6">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div>
                  <label className="form-label">Expense ID</label>
                  <p className="text-gray-900 dark:text-white font-mono">#{selectedExpense.expenseId}</p>
                </div>
                
                <div>
                  <label className="form-label">Name</label>
                  <p className="text-gray-900 dark:text-white">{selectedExpense.name}</p>
                </div>
                
                <div>
                  <label className="form-label">Amount</label>
                  <p className="text-green-600 dark:text-green-400 font-mono text-lg font-medium">
                    Rs {selectedExpense.amount.toLocaleString()}
                  </p>
                </div>
                
                <div>
                  <label className="form-label">Expense Type</label>
                  <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-blue-100 text-blue-800 dark:bg-blue-900 dark:text-blue-200">
                    {selectedExpense.expenseTypeName}
                  </span>
                </div>
                
                <div>
                  <label className="form-label">Created By</label>
                  <p className="text-gray-900 dark:text-white">{selectedExpense.userName}</p>
                </div>
                
                <div>
                  <label className="form-label">User Role</label>
                  <p className="text-gray-600 dark:text-gray-400">{selectedExpense.userRole}</p>
                </div>
                
                <div>
                  <label className="form-label">Created Date</label>
                  <p className="text-gray-600 dark:text-gray-400">
                    {new Date(selectedExpense.createdAt).toLocaleDateString()}
                  </p>
                </div>
                
                <div>
                  <label className="form-label">Created Time</label>
                  <p className="text-gray-600 dark:text-gray-400">
                    {new Date(selectedExpense.createdAt).toLocaleTimeString()}
                  </p>
                </div>
                
                {selectedExpense.updatedAt && selectedExpense.updatedAt !== selectedExpense.createdAt && (
                  <>
                    <div>
                      <label className="form-label">Last Updated</label>
                      <p className="text-gray-600 dark:text-gray-400">
                        {new Date(selectedExpense.updatedAt).toLocaleDateString()}
                      </p>
                    </div>
                    
                    <div>
                      <label className="form-label">Update Time</label>
                      <p className="text-gray-600 dark:text-gray-400">
                        {new Date(selectedExpense.updatedAt).toLocaleTimeString()}
                      </p>
                    </div>
                  </>
                )}
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Edit Modal */}
      {showEditModal && selectedExpense && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white dark:bg-gray-800 rounded-lg shadow-xl max-w-2xl w-full mx-4 max-h-[90vh] overflow-y-auto">
            <div className="flex items-center justify-between p-6 border-b border-gray-200 dark:border-gray-700">
              <h3 className="text-lg font-medium text-gray-900 dark:text-white">Edit Expense</h3>
              <Button
                variant="ghost"
                size="sm"
                onClick={closeModal}
                leftIcon={<X className="h-4 w-4" />}
              >
                Close
              </Button>
            </div>
            
            <form onSubmit={handleEditSubmit} className="p-6 space-y-6">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div>
                  <label className="form-label">Expense ID</label>
                  <p className="text-gray-600 dark:text-gray-400 font-mono">#{selectedExpense.expenseId}</p>
                </div>
                
                <div>
                  <label className="form-label">Description *</label>
                  <Input
                    type="text"
                    value={editFormData.name}
                    onChange={(e) => setEditFormData(prev => ({ ...prev, name: e.target.value }))}
                    placeholder="Expense description"
                    required
                  />
                </div>
                
                <div>
                  <label className="form-label">Amount *</label>
                  <Input
                    type="number"
                    value={editFormData.amount}
                    onChange={(e) => setEditFormData(prev => ({ ...prev, amount: e.target.value }))}
                    placeholder="0"
                    min="0"
                    step="1"
                    required
                  />
                </div>
                
                <div>
                  <label className="form-label">Expense Type *</label>
                  <select
                    value={editFormData.expenseTypeId}
                    onChange={(e) => setEditFormData(prev => ({ ...prev, expenseTypeId: e.target.value }))}
                    className="form-select"
                    required
                  >
                    <option value="">Select expense type</option>
                    {expenseTypes.map((type) => (
                      <option key={type._id} value={type._id}>
                        {type.name}
                      </option>
                    ))}
                  </select>
                </div>
                
                <div>
                  <label className="form-label">Account *</label>
                  <select
                    value={editFormData.accountId}
                    onChange={(e) => setEditFormData(prev => ({ ...prev, accountId: e.target.value }))}
                    className="form-select"
                    required
                  >
                    <option value="">Select account</option>
                    {accounts.map((account) => (
                      <option key={account._id} value={account._id}>
                        {account.name} ({account.accountNumber}) - Rs. {account.balance.toLocaleString()}
                      </option>
                    ))}
                  </select>
                </div>
              </div>
              
              <div className="flex justify-end space-x-3 pt-6 border-t border-gray-200 dark:border-gray-600">
                <Button type="button" variant="secondary" onClick={closeModal}>
                  Cancel
                </Button>
                <Button type="submit" variant="primary">
                  Update Expense
                </Button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
};

export default ManageExpenses;
